import React from "react";
import { Link } from "react-router-dom";
import { IoIosEye, IoIosWallet, IoIosCash, IoIosAttach } from "react-icons/io";
import { FiArrowUpRight, FiArrowDownLeft } from "react-icons/fi";
import {
  usePublicShareSummary,
  usePublicShareTransactions,
} from "../../api";
import { FormatNumber, FormatDate } from "../../helpers";
import "./share.css";

function ViewerNotFound() {
  return (
    <div className="viewer__not-found">
      <IoIosEye size="2.5em" color="var(--ink-faint)" />
      <h1>Link tidak ditemukan</h1>
      <p>
        Link berbagi ini sudah dicabut, sudah diganti, atau tidak pernah ada.
        Hubungi pengirim untuk meminta link baru.
      </p>
    </div>
  );
}

export default function Viewer(props) {
  const token = props.match.params.token;
  const { data: summary, error } = usePublicShareSummary(token);

  // Set robots noindex on viewer pages
  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  React.useEffect(() => {
    if (summary && summary.orgName) {
      document.title = `${summary.orgName} · Cashflow`;
    }
  }, [summary]);

  if (error && error.response && error.response.status === 404) {
    return <ViewerNotFound />;
  }

  if (!summary) {
    return (
      <div className="viewer">
        <div className="viewer__topbar">
          <span className="viewer__topbar-badge">
            <IoIosEye /> Read-only
          </span>
          <span className="viewer__topbar-org">Memuat…</span>
        </div>
      </div>
    );
  }

  const total = summary.totalBalance || 0;
  const debt = summary.debtSummary || {};
  const hasDebt = (debt.openUtangCount || 0) + (debt.openPiutangCount || 0) > 0;

  return (
    <div className="viewer">
      <header className="viewer__topbar">
        <span className="viewer__topbar-badge">
          <IoIosEye /> Read-only
        </span>
        <span className="viewer__topbar-org">{summary.orgName}</span>
        <span className="viewer__topbar-hint">via Cashflow</span>
      </header>

      <div className="viewer__container">
        <section className="viewer__hero">
          <div className="viewer__hero-label">Total Saldo Wallet</div>
          <div
            className={`viewer__hero-amount ${
              total < 0 ? "viewer__hero-amount--negative" : ""
            }`}
          >
            {total < 0 ? "−" : ""}Rp{" "}
            {FormatNumber(Math.abs(Math.round(total)))}
          </div>
          <div className="viewer__hero-sub">
            {summary.walletCount} wallet · {summary.activeWalletCount} aktif
          </div>
        </section>

        <div className="viewer__grid">
          <div className="viewer__stat">
            <div className="viewer__stat-label">
              <IoIosCash /> Uang Tunai
            </div>
            <div className="viewer__stat-value">
              Rp {FormatNumber(Math.round(summary.cashTotal || 0))}
            </div>
          </div>
          <div className="viewer__stat">
            <div className="viewer__stat-label">Wallet</div>
            <div className="viewer__stat-value">{summary.walletCount}</div>
          </div>
        </div>

        {hasDebt && (
          <div className="viewer__grid">
            <div className="viewer__stat">
              <div className="viewer__stat-label">
                <FiArrowUpRight /> Utang
              </div>
              <div className="viewer__stat-value">
                Rp {FormatNumber(Math.round(debt.totalUtangOutstanding || 0))}
              </div>
            </div>
            <div className="viewer__stat">
              <div className="viewer__stat-label">
                <FiArrowDownLeft /> Piutang
              </div>
              <div className="viewer__stat-value">
                Rp {FormatNumber(Math.round(debt.totalPiutangOutstanding || 0))}
              </div>
            </div>
          </div>
        )}

        <ViewerWallets wallets={summary.wallets || []} />
        <ViewerRecentTransactions token={token} />

        <div className="viewer__footer">
          Catatan ini dibagikan oleh{" "}
          <Link to="/landing" style={{ color: "var(--brand-800)" }}>
            Cashflow
          </Link>
        </div>
      </div>
    </div>
  );
}

function ViewerWallets({ wallets }) {
  if (!wallets || wallets.length === 0) return null;
  return (
    <>
      <div className="viewer__section-title">
        <span>Saldo Tiap Wallet</span>
      </div>
      <ul className="wallet-list">
        {wallets.map((w) => {
          const sum = w.sum || 0;
          const isNeg = sum < 0;
          const isZero = sum === 0;
          return (
            <li
              key={w.id}
              className={`wallet-card ${isNeg ? "wallet-card--negative" : ""}`}
              style={{ cursor: "default" }}
            >
              <span className="wallet-card__rail" aria-hidden />
              <span className="wallet-card__icon">
                <IoIosWallet />
              </span>
              <div className="wallet-card__body">
                <div className="wallet-card__name">{w.name}</div>
              </div>
              <div className="wallet-card__right">
                <div
                  className={`wallet-card__amount ${
                    isZero ? "wallet-card__amount--zero" : ""
                  }`}
                >
                  {isNeg ? "−" : ""}Rp{" "}
                  {FormatNumber(Math.abs(Math.round(sum)))}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function ViewerRecentTransactions({ token }) {
  const { data, error } = usePublicShareTransactions(token, { limit: 20 });
  if (error) {
    return (
      <>
        <div className="viewer__section-title">
          <span>Transaksi Terbaru</span>
        </div>
        <div className="viewer__empty">Gagal memuat transaksi.</div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <div className="viewer__section-title">
          <span>Transaksi Terbaru</span>
        </div>
        <div className="viewer__empty">Memuat…</div>
      </>
    );
  }
  const items = (data && data.content) || [];
  return (
    <>
      <div className="viewer__section-title">
        <span>Transaksi Terbaru</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="viewer__empty">Belum ada transaksi.</div>
      ) : (
        items.map((t) => {
          const isIn = (t.amount || 0) >= 0;
          const tags = t.tags || [];
          return (
            <div
              key={t.id}
              className={`trx-card trx-card--${isIn ? "in" : "out"}`}
              style={{ cursor: "default" }}
            >
              <span className="trx-card__rail" aria-hidden />
              <div className="trx-card__body">
                <div className="trx-card__row trx-card__row--primary">
                  <div className="trx-card__desc">
                    {t.description || (isIn ? "Pemasukan" : "Pengeluaran")}
                  </div>
                  <div className="trx-card__amount">
                    {isIn ? "+" : "−"}Rp{" "}
                    {FormatNumber(Math.abs(Math.round(t.amount)))}
                  </div>
                </div>
                <div className="trx-card__row trx-card__row--meta">
                  <div className="trx-card__metainfo">
                    <span className="trx-card__date">
                      {FormatDate(t.date)}
                    </span>
                    <span className="trx-card__sep" aria-hidden>
                      ·
                    </span>
                    <span className="trx-card__wallet">
                      <IoIosWallet /> {t.wallet && t.wallet.name}
                    </span>
                    {tags.length > 0 && (
                      <span className="trx-card__tags">
                        {tags.map((tg, i) => (
                          <span
                            key={i}
                            className="trx-tag trx-tag--mini"
                            style={{ cursor: "default" }}
                          >
                            {tg.tag}
                          </span>
                        ))}
                      </span>
                    )}
                    {t.attachmentUrl && (
                      <a
                        href={t.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "var(--brand-700)",
                          fontSize: "0.75em",
                          marginLeft: "0.25rem",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.15rem",
                        }}
                      >
                        <IoIosAttach /> lampiran
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </>
  );
}
